import { useState, useEffect } from 'react';
import api from '../services/api';
import { getStoredHotelId } from '../services/authService';

interface Transaction {
  id: string;
  invoice_number?: string;
  amount?: number;
  currency?: string;
  source_file?: string;
  status?: string;
  created_at?: string;
}

export default function CollectorDashboardPage() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const hotelId = getStoredHotelId();
    if (!hotelId) {
      setError('Identifiant hôtel introuvable');
      setLoading(false);
      return;
    }
    api.get(`/api/v1/fpt/transactions/hotel/${hotelId}`)
      .then(res => {
        const data = res.data?.data ?? res.data?.transactions ?? res.data;
        setTransactions(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        const msg = err?.response?.data?.error || err?.message || 'Erreur de chargement';
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      })
      .finally(() => setLoading(false));
  }, []);

  const total = transactions.length;
  const totalAmount = transactions.reduce((s, t) => s + (t.amount || 0), 0);
  const pending = transactions.filter(t => t.status === 'PENDING' || t.status === 'pending').length;

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">DIEF Collector</h2>
        <p className="text-sm text-gray-500 mt-1">
          Transactions POS collectées automatiquement par l'agent DIEF.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Transactions collectées</p>
          <p className="text-2xl font-bold text-gray-900">{total}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">Montant total</p>
          <p className="text-2xl font-bold text-gray-900">
            {totalAmount.toLocaleString('fr-FR')} USD
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm text-gray-500">En attente</p>
          <p className="text-2xl font-bold text-orange-600">{pending}</p>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500 animate-pulse">
          Chargement des transactions...
        </div>
      ) : transactions.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 text-gray-500 px-4 py-12 rounded text-center">
          <p className="font-medium mb-1">Aucune transaction collectée</p>
          <p className="text-sm">
            L'agent DIEF n'a pas encore transmis de transactions pour cet établissement.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500 text-xs">
                <th className="p-3">N° Facture</th>
                <th className="p-3">Montant</th>
                <th className="p-3">Devise</th>
                <th className="p-3">Source</th>
                <th className="p-3">Statut</th>
                <th className="p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.slice(0, 50).map(t => (
                <tr key={t.id} className="border-b hover:bg-gray-50">
                  <td className="p-3 font-medium">{t.invoice_number || '—'}</td>
                  <td className="p-3">{t.amount != null ? t.amount.toLocaleString('fr-FR') : '—'}</td>
                  <td className="p-3">{t.currency || 'USD'}</td>
                  <td className="p-3 text-xs text-gray-500">{t.source_file || '—'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      t.status === 'PROCESSED' ? 'bg-green-100 text-green-800' :
                      t.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {t.status || '—'}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-gray-500">
                    {t.created_at ? new Date(t.created_at).toLocaleString('fr-FR') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
